import { useEffect, useState } from 'react';

const validSize = (value) => Number.isFinite(value) && value > 0;

// Measurement affects label density, never whether an otherwise valid SVG paints.
export const useChartLayout = (containerRef, { accountId, periodKey, data, visibleSeries }) => {
    const [layout, setLayout] = useState({ width: 0, height: 0, ready: false });

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;
        let disposed = false;
        let retry = null;
        let frame = null;

        const measure = () => {
            if (disposed) return;
            const rect = container.getBoundingClientRect();
            const width = validSize(rect.width) ? rect.width : 0;
            const height = validSize(rect.height) ? rect.height : 0;
            const ready = width > 0 && height > 0;
            setLayout((previous) => previous.width === width && previous.height === height
                && previous.ready === ready ? previous : { width, height, ready });
            if (retry !== null) clearTimeout(retry);
            // Hidden mounts can miss their first observer callback. Retry only while
            // invalid, without blocking rendering or polling a resolved chart.
            retry = ready ? null : setTimeout(measure, 250);
        };
        measure();
        if (typeof requestAnimationFrame === 'function') frame = requestAnimationFrame(measure);

        let observer;
        if (typeof ResizeObserver === 'function') {
            try {
                observer = new ResizeObserver(measure);
                observer.observe(container);
            } catch {
                observer?.disconnect();
                observer = null;
            }
        }
        // Attribute changes recover hidden panels on engines without ResizeObserver.
        let visibilityObserver;
        if (!observer && typeof MutationObserver === 'function') {
            visibilityObserver = new MutationObserver(measure);
            for (let node = container; node; node = node.parentElement) {
                visibilityObserver.observe(node, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
            }
        }
        window.addEventListener('resize', measure);
        window.addEventListener('orientationchange', measure);
        window.addEventListener('pageshow', measure);
        document.addEventListener('visibilitychange', measure);

        return () => {
            disposed = true;
            if (retry !== null) clearTimeout(retry);
            if (frame !== null) cancelAnimationFrame(frame);
            observer?.disconnect();
            visibilityObserver?.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('orientationchange', measure);
            window.removeEventListener('pageshow', measure);
            document.removeEventListener('visibilitychange', measure);
        };
    }, [containerRef, accountId, periodKey, data, visibleSeries]);

    return layout;
};
